import { useMemo } from "react";
import type { MutableRefObject } from "react";

export type GameSkillsSnapshot = {
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
};

export type GameAudiosSnapshot = {
    audio_question: boolean;
    audio_choice: boolean;
    audio_assistant_voice: boolean;
    audio_background: boolean;
};

export type GameMetaSnapshot = {
    exerciseName: string;
    suggestion: string;
    description: string;
};

export const PREVIEW_UNSAVED_TITLE = "ไม่สามารถ Preview ได้";
export const PREVIEW_UNSAVED_MESSAGE =
    "มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก กรุณากดบันทึกก่อน Preview";
export const PREVIEW_BUTTON_DISABLED_TITLE = "กรุณาบันทึกก่อน Preview";

export const GAME_PREVIEW_BUTTON_CLASS =
    "cursor-pointer rounded bg-[#4E4E4E] px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#3d3d3d] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:hover:bg-gray-200";

function hasPendingUpload(...files: Array<File | null | undefined>) {
    return files.some(Boolean);
}

export function areGameFormSnapshotsEqual<T>(savedSnapshot: T | null, currentSnapshot: T) {
    if (!savedSnapshot) {
        return false;
    }

    return JSON.stringify(savedSnapshot) === JSON.stringify(currentSnapshot);
}

export function useHasUnsavedGameFormChanges<T>(
    isUpdateMode: boolean,
    currentSnapshot: T,
    savedSnapshotRef: MutableRefObject<T | null>
) {
    return useMemo(() => {
        if (!isUpdateMode) {
            return false;
        }

        return !areGameFormSnapshotsEqual(savedSnapshotRef.current, currentSnapshot);
    }, [isUpdateMode, currentSnapshot, savedSnapshotRef]);
}

export function getCanGamePreview(
    isUpdateMode: boolean,
    previewUuid: string,
    hasUnsavedChanges: boolean
) {
    return isUpdateMode && Boolean(previewUuid.trim()) && !hasUnsavedChanges;
}

export function getShouldConfirmLeaveGameEditor(
    isUpdateMode: boolean,
    hasUnsavedChanges: boolean
) {
    return !isUpdateMode || hasUnsavedChanges;
}

export function createGameMetaSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string
): GameMetaSnapshot {
    return {
        exerciseName: exerciseName.trim(),
        suggestion: suggestion.trim(),
        description: description.trim(),
    };
}

type MatchingItemLike = {
    id: number;
    keyword: string;
    matchingWord: string;
    hasHint: boolean;
    hintText: string;
    imageKeywordFile?: File | null;
    audioKeywordFile?: File | null;
    imageMatchingFile?: File | null;
    audioMatchingFile?: File | null;
    hintImageFile?: File | null;
    hintAudioFile?: File | null;
    imageKeywordUrl: string | null;
    audioKeywordUrl: string | null;
    imageMatchingUrl: string | null;
    audioMatchingUrl: string | null;
    hintImageUrl: string | null;
    hintAudioUrl: string | null;
};

export type MatchingItemsGameSnapshot = ReturnType<typeof createMatchingItemsGameSnapshot>;

export type MatchingItemsMediaDeleteField =
    | "sound_keyword"
    | "sound_matching_word"
    | "image_keyword"
    | "image_matching_word"
    | "hint"
    | "sound_hint"
    | "image_hint";

function patchMatchingSnapshotItemAfterMediaDeleted(
    item: MatchingItemsGameSnapshot["items"][number],
    field: MatchingItemsMediaDeleteField
) {
    switch (field) {
        case "image_keyword":
            return { ...item, imageKeywordUrl: null };
        case "sound_keyword":
            return { ...item, audioKeywordUrl: null };
        case "image_matching_word":
            return { ...item, imageMatchingUrl: null };
        case "sound_matching_word":
            return { ...item, audioMatchingUrl: null };
        case "image_hint":
            return { ...item, hintImageUrl: null };
        case "sound_hint":
            return { ...item, hintAudioUrl: null };
        case "hint":
            return {
                ...item,
                hasHint: false,
                hintText: "",
                hintImageUrl: null,
                hintAudioUrl: null,
            };
        default:
            return item;
    }
}

export function patchSavedMatchingItemsSnapshotAfterItemRemoved(
    savedSnapshotRef: MutableRefObject<MatchingItemsGameSnapshot | null>,
    removedItemId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.filter((item) => item.id !== removedItemId),
    };
}

export function patchSavedMatchingItemsSnapshotAfterMediaDeleted(
    savedSnapshotRef: MutableRefObject<MatchingItemsGameSnapshot | null>,
    itemId: number,
    field: MatchingItemsMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.map((item) =>
            item.id === itemId ? patchMatchingSnapshotItemAfterMediaDeleted(item, field) : item
        ),
    };
}

export function createMatchingItemsGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceItems: MatchingItemLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        items: sourceItems.map((item) => ({
            id: item.id,
            keyword: item.keyword,
            matchingWord: item.matchingWord,
            hasHint: item.hasHint,
            hintText: item.hintText,
            imageKeywordUrl: item.imageKeywordUrl,
            audioKeywordUrl: item.audioKeywordUrl,
            imageMatchingUrl: item.imageMatchingUrl,
            audioMatchingUrl: item.audioMatchingUrl,
            hintImageUrl: item.hintImageUrl,
            hintAudioUrl: item.hintAudioUrl,
            hasPendingUpload: hasPendingUpload(
                item.imageKeywordFile,
                item.audioKeywordFile,
                item.imageMatchingFile,
                item.audioMatchingFile,
                item.hintImageFile,
                item.hintAudioFile
            ),
        })),
    };
}

type AnagramItemLike = {
    id: number;
    word: string;
    swapWord: string[];
    imageWordFile?: File | null;
    audioWordFile?: File | null;
    imageSwapFile?: File | null;
    audioSwapFile?: File | null;
    hintImageFile?: File | null;
    hintAudioFile?: File | null;
    imageWordUrl: string | null;
    audioWordUrl: string | null;
    imageSwapUrl: string | null;
    audioSwapUrl: string | null;
    hasHint: boolean;
    hintText: string;
    hintImageUrl: string | null;
    hintAudioUrl: string | null;
};

export type AnagramItemsGameSnapshot = ReturnType<typeof createAnagramItemsGameSnapshot>;

export type AnagramItemsMediaDeleteField =
    | "sound_word"
    | "sound_swap_word"
    | "image_word"
    | "image_swap_word"
    | "hint"
    | "sound_hint"
    | "image_hint";

function patchAnagramSnapshotItemAfterMediaDeleted(
    item: AnagramItemsGameSnapshot["items"][number],
    field: AnagramItemsMediaDeleteField
) {
    switch (field) {
        case "image_word":
            return { ...item, imageWordUrl: null };
        case "sound_word":
            return { ...item, audioWordUrl: null };
        case "image_swap_word":
            return { ...item, imageSwapUrl: null };
        case "sound_swap_word":
            return { ...item, audioSwapUrl: null };
        case "image_hint":
            return { ...item, hintImageUrl: null };
        case "sound_hint":
            return { ...item, hintAudioUrl: null };
        case "hint":
            return {
                ...item,
                hasHint: false,
                hintText: "",
                hintImageUrl: null,
                hintAudioUrl: null,
            };
        default:
            return item;
    }
}

export function patchSavedAnagramItemsSnapshotAfterItemRemoved(
    savedSnapshotRef: MutableRefObject<AnagramItemsGameSnapshot | null>,
    removedItemId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.filter((item) => item.id !== removedItemId),
    };
}

export function patchSavedAnagramItemsSnapshotAfterMediaDeleted(
    savedSnapshotRef: MutableRefObject<AnagramItemsGameSnapshot | null>,
    itemId: number,
    field: AnagramItemsMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.map((item) =>
            item.id === itemId ? patchAnagramSnapshotItemAfterMediaDeleted(item, field) : item
        ),
    };
}

export function createAnagramItemsGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceItems: AnagramItemLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        items: sourceItems.map((item) => ({
            id: item.id,
            word: item.word,
            swapWord: [...item.swapWord],
            hasHint: item.hasHint,
            hintText: item.hintText,
            imageWordUrl: item.imageWordUrl,
            audioWordUrl: item.audioWordUrl,
            imageSwapUrl: item.imageSwapUrl,
            audioSwapUrl: item.audioSwapUrl,
            hintImageUrl: item.hintImageUrl,
            hintAudioUrl: item.hintAudioUrl,
            hasPendingUpload: hasPendingUpload(
                item.imageWordFile,
                item.audioWordFile,
                item.imageSwapFile,
                item.audioSwapFile,
                item.hintImageFile,
                item.hintAudioFile
            ),
        })),
    };
}

type WhackAMoleItemLike = {
    id: number;
    correctAnswer: string;
    wrongAnswer: string;
    imageCorrectAnswerFile?: File | null;
    soundCorrectAnswerFile?: File | null;
    imageWrongAnswerFile?: File | null;
    soundWrongAnswerFile?: File | null;
    imageCorrectAnswerUrl: string | null;
    soundCorrectAnswerUrl: string | null;
    imageWrongAnswerUrl: string | null;
    soundWrongAnswerUrl: string | null;
};

export type WhackAMoleItemsGameSnapshot = ReturnType<typeof createWhackAMoleItemsGameSnapshot>;

export type WhackAMoleItemsMediaDeleteField =
    | "image_correct_answer"
    | "sound_correct_answer"
    | "image_wrong_answer"
    | "sound_wrong_answer";

function patchWhackAMoleSnapshotItemAfterMediaDeleted(
    item: WhackAMoleItemsGameSnapshot["items"][number],
    field: WhackAMoleItemsMediaDeleteField
) {
    switch (field) {
        case "image_correct_answer":
            return { ...item, imageCorrectAnswerUrl: null };
        case "sound_correct_answer":
            return { ...item, soundCorrectAnswerUrl: null };
        case "image_wrong_answer":
            return { ...item, imageWrongAnswerUrl: null };
        case "sound_wrong_answer":
            return { ...item, soundWrongAnswerUrl: null };
        default:
            return item;
    }
}

export function patchSavedWhackAMoleItemsSnapshotAfterItemRemoved(
    savedSnapshotRef: MutableRefObject<WhackAMoleItemsGameSnapshot | null>,
    removedItemId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.filter((item) => item.id !== removedItemId),
    };
}

export function patchSavedWhackAMoleItemsSnapshotAfterMediaDeleted(
    savedSnapshotRef: MutableRefObject<WhackAMoleItemsGameSnapshot | null>,
    itemId: number,
    field: WhackAMoleItemsMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.map((item) =>
            item.id === itemId ? patchWhackAMoleSnapshotItemAfterMediaDeleted(item, field) : item
        ),
    };
}

export function createWhackAMoleItemsGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceItems: WhackAMoleItemLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        items: sourceItems.map((item) => ({
            id: item.id,
            correctAnswer: item.correctAnswer,
            wrongAnswer: item.wrongAnswer,
            imageCorrectAnswerUrl: item.imageCorrectAnswerUrl,
            soundCorrectAnswerUrl: item.soundCorrectAnswerUrl,
            imageWrongAnswerUrl: item.imageWrongAnswerUrl,
            soundWrongAnswerUrl: item.soundWrongAnswerUrl,
            hasPendingUpload: hasPendingUpload(
                item.imageCorrectAnswerFile,
                item.soundCorrectAnswerFile,
                item.imageWrongAnswerFile,
                item.soundWrongAnswerFile
            ),
        })),
    };
}

type CompleteTheSentenceSegmentLike = {
    correctAnswer: string;
};

type CompleteTheSentenceItemLike = {
    id: number;
    sentenceText: string;
    segments: CompleteTheSentenceSegmentLike[];
    wrongChoices: string[];
    hasHint: boolean;
    hintText: string;
    imageQuestionFile?: File | null;
    audioQuestionFile?: File | null;
    imageHintFile?: File | null;
    audioHintFile?: File | null;
    imageQuestionUrl: string | null;
    audioQuestionUrl: string | null;
    imageHintUrl: string | null;
    audioHintUrl: string | null;
};

export type CompleteTheSentenceItemsGameSnapshot = ReturnType<
    typeof createCompleteTheSentenceItemsGameSnapshot
>;

export type CompleteTheSentenceItemsMediaDeleteField =
    | "sound_question"
    | "image_question"
    | "hint"
    | "sound_hint"
    | "image_hint";

function patchCompleteTheSentenceSnapshotItemAfterMediaDeleted(
    item: CompleteTheSentenceItemsGameSnapshot["items"][number],
    field: CompleteTheSentenceItemsMediaDeleteField
) {
    switch (field) {
        case "image_question":
            return { ...item, imageQuestionUrl: null };
        case "sound_question":
            return { ...item, audioQuestionUrl: null };
        case "image_hint":
            return { ...item, imageHintUrl: null };
        case "sound_hint":
            return { ...item, audioHintUrl: null };
        case "hint":
            return {
                ...item,
                hasHint: false,
                hintText: "",
                imageHintUrl: null,
                audioHintUrl: null,
            };
        default:
            return item;
    }
}

export function patchSavedCompleteTheSentenceItemsSnapshotAfterItemRemoved(
    savedSnapshotRef: MutableRefObject<CompleteTheSentenceItemsGameSnapshot | null>,
    removedItemId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.filter((item) => item.id !== removedItemId),
    };
}

export function patchSavedCompleteTheSentenceItemsSnapshotAfterMediaDeleted(
    savedSnapshotRef: MutableRefObject<CompleteTheSentenceItemsGameSnapshot | null>,
    itemId: number,
    field: CompleteTheSentenceItemsMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        items: savedSnapshotRef.current.items.map((item) =>
            item.id === itemId
                ? patchCompleteTheSentenceSnapshotItemAfterMediaDeleted(item, field)
                : item
        ),
    };
}

export function createCompleteTheSentenceItemsGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceItems: CompleteTheSentenceItemLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        items: sourceItems.map((item) => ({
            id: item.id,
            sentenceText: item.sentenceText,
            segments: item.segments.map((segment) => ({
                correctAnswer: segment.correctAnswer,
            })),
            wrongChoices: [...item.wrongChoices],
            hasHint: item.hasHint,
            hintText: item.hintText,
            imageQuestionUrl: item.imageQuestionUrl,
            audioQuestionUrl: item.audioQuestionUrl,
            imageHintUrl: item.imageHintUrl,
            audioHintUrl: item.audioHintUrl,
            hasPendingUpload: hasPendingUpload(
                item.imageQuestionFile,
                item.audioQuestionFile,
                item.imageHintFile,
                item.audioHintFile
            ),
        })),
    };
}

type QuizAnswerLike = {
    id: number;
    text: string;
    isCorrect: boolean;
    imageFile?: File | null;
    audioFile?: File | null;
    imageUrl: string | null;
    audioUrl: string | null;
};

type QuizQuestionLike = {
    id: number;
    text: string;
    shuffle_answer: boolean;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    hasHint: boolean;
    hintText: string;
    imageFile?: File | null;
    audioFile?: File | null;
    hintImageFile?: File | null;
    hintAudioFile?: File | null;
    imageUrl: string | null;
    audioUrl: string | null;
    hintImageUrl: string | null;
    hintAudioUrl: string | null;
    position?: { x: number; y: number } | null;
    answers: QuizAnswerLike[];
};

function mapQuizQuestionSnapshot(question: QuizQuestionLike) {
    return {
        id: question.id,
        text: question.text,
        shuffle_answer: question.shuffle_answer,
        skill_listening_speaking: question.skill_listening_speaking,
        skill_lang_nature: question.skill_lang_nature,
        skill_reading: question.skill_reading,
        skill_writing: question.skill_writing,
        hasHint: question.hasHint,
        hintText: question.hintText,
        imageUrl: question.imageUrl,
        audioUrl: question.audioUrl,
        hintImageUrl: question.hintImageUrl,
        hintAudioUrl: question.hintAudioUrl,
        position: question.position ? { ...question.position } : null,
        hasPendingUpload: hasPendingUpload(
            question.imageFile,
            question.audioFile,
            question.hintImageFile,
            question.hintAudioFile
        ),
        answers: question.answers.map((answer) => ({
            id: answer.id,
            text: answer.text,
            isCorrect: answer.isCorrect,
            imageUrl: answer.imageUrl,
            audioUrl: answer.audioUrl,
            hasPendingUpload: hasPendingUpload(answer.imageFile, answer.audioFile),
        })),
    };
}

export function createQuizQuestionsGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceQuestions: QuizQuestionLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        questions: sourceQuestions.map(mapQuizQuestionSnapshot),
    };
}

export type QuizQuestionsGameSnapshot = ReturnType<typeof createQuizQuestionsGameSnapshot>;

export function commitSavedQuizQuestionsGameSnapshot(
    savedSnapshotRef: MutableRefObject<QuizQuestionsGameSnapshot | null>,
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    questions: QuizQuestionLike[]
) {
    savedSnapshotRef.current = createQuizQuestionsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        questions
    );
}

export type QuizQuestionMediaDeleteField =
    | "sound_question"
    | "image_question"
    | "hint"
    | "sound_hint"
    | "image_hint";

export type QuizChoiceMediaDeleteField = "sound_choice" | "image_choice";

type QuizQuestionSnapshotItem = QuizQuestionsGameSnapshot["questions"][number];
type QuizChoiceSnapshotItem = QuizQuestionSnapshotItem["answers"][number];

function patchQuizSnapshotQuestionAfterMediaDeleted(
    question: QuizQuestionSnapshotItem,
    field: QuizQuestionMediaDeleteField
): QuizQuestionSnapshotItem {
    switch (field) {
        case "image_question":
            return { ...question, imageUrl: null };
        case "sound_question":
            return { ...question, audioUrl: null };
        case "image_hint":
            return { ...question, hintImageUrl: null };
        case "sound_hint":
            return { ...question, hintAudioUrl: null };
        case "hint":
            return {
                ...question,
                hasHint: false,
                hintText: "",
                hintImageUrl: null,
                hintAudioUrl: null,
            };
        default:
            return question;
    }
}

function patchQuizSnapshotChoiceAfterMediaDeleted(
    choice: QuizChoiceSnapshotItem,
    field: QuizChoiceMediaDeleteField
): QuizChoiceSnapshotItem {
    switch (field) {
        case "image_choice":
            return { ...choice, imageUrl: null };
        case "sound_choice":
            return { ...choice, audioUrl: null };
        default:
            return choice;
    }
}

function patchQuizQuestionsSnapshotArrayAfterQuestionRemoved(
    questions: QuizQuestionSnapshotItem[],
    removedQuestionId: number
) {
    return questions.filter((question) => question.id !== removedQuestionId);
}

function patchQuizQuestionsSnapshotArrayAfterChoiceRemoved(
    questions: QuizQuestionSnapshotItem[],
    questionId: number,
    removedChoiceId: number
) {
    return questions.map((question) =>
        question.id === questionId
            ? {
                ...question,
                answers: question.answers.filter((answer) => answer.id !== removedChoiceId),
            }
            : question
    );
}

function patchQuizQuestionsSnapshotArrayAfterQuestionMediaDeleted(
    questions: QuizQuestionSnapshotItem[],
    questionId: number,
    field: QuizQuestionMediaDeleteField
) {
    return questions.map((question) =>
        question.id === questionId
            ? patchQuizSnapshotQuestionAfterMediaDeleted(question, field)
            : question
    );
}

function patchQuizQuestionsSnapshotArrayAfterChoiceMediaDeleted(
    questions: QuizQuestionSnapshotItem[],
    questionId: number,
    choiceId: number,
    field: QuizChoiceMediaDeleteField
) {
    return questions.map((question) =>
        question.id === questionId
            ? {
                ...question,
                answers: question.answers.map((answer) =>
                    answer.id === choiceId
                        ? patchQuizSnapshotChoiceAfterMediaDeleted(answer, field)
                        : answer
                ),
            }
            : question
    );
}

export function patchSavedQuizQuestionsSnapshotAfterQuestionRemoved(
    savedSnapshotRef: MutableRefObject<QuizQuestionsGameSnapshot | null>,
    removedQuestionId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterQuestionRemoved(
            savedSnapshotRef.current.questions,
            removedQuestionId
        ),
    };
}

export function patchSavedQuizQuestionsSnapshotAfterChoiceRemoved(
    savedSnapshotRef: MutableRefObject<QuizQuestionsGameSnapshot | null>,
    questionId: number,
    removedChoiceId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterChoiceRemoved(
            savedSnapshotRef.current.questions,
            questionId,
            removedChoiceId
        ),
    };
}

export function patchSavedQuizQuestionsSnapshotAfterQuestionMediaDeleted(
    savedSnapshotRef: MutableRefObject<QuizQuestionsGameSnapshot | null>,
    questionId: number,
    field: QuizQuestionMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterQuestionMediaDeleted(
            savedSnapshotRef.current.questions,
            questionId,
            field
        ),
    };
}

export function patchSavedQuizQuestionsSnapshotAfterChoiceMediaDeleted(
    savedSnapshotRef: MutableRefObject<QuizQuestionsGameSnapshot | null>,
    questionId: number,
    choiceId: number,
    field: QuizChoiceMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterChoiceMediaDeleted(
            savedSnapshotRef.current.questions,
            questionId,
            choiceId,
            field
        ),
    };
}

type SituationAssetLike = {
    id: number;
    position: { x: number; y: number };
    imageFile?: File | null;
    imageUrl: string | null;
};

export function createSituationGameSnapshot(
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    sourceQuestions: QuizQuestionLike[],
    positionImageUrl: string,
    positionImageFile: File | null,
    sourceAssets: SituationAssetLike[]
) {
    return {
        ...createGameMetaSnapshot(exerciseName, suggestion, description),
        gameSkills: { ...gameSkills },
        gameAudios: { ...gameAudios },
        positionImage: {
            url: positionImageFile ? null : positionImageUrl.trim() || null,
            hasPendingUpload: Boolean(positionImageFile),
        },
        positionAssets: sourceAssets.map((asset) => ({
            id: asset.id,
            position: { ...asset.position },
            imageUrl: asset.imageUrl,
            hasPendingUpload: Boolean(asset.imageFile),
        })),
        questions: sourceQuestions.map(mapQuizQuestionSnapshot),
    };
}

export type SituationGameSnapshot = ReturnType<typeof createSituationGameSnapshot>;

export function commitSavedSituationGameSnapshot(
    savedSnapshotRef: MutableRefObject<SituationGameSnapshot | null>,
    exerciseName: string,
    suggestion: string,
    description: string,
    gameSkills: GameSkillsSnapshot,
    gameAudios: GameAudiosSnapshot,
    questions: QuizQuestionLike[],
    positionImageUrl: string,
    positionImageFile: File | null,
    sourceAssets: SituationAssetLike[]
) {
    savedSnapshotRef.current = createSituationGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        questions,
        positionImageUrl,
        positionImageFile,
        sourceAssets
    );
}

export function patchSavedSituationGameSnapshotAfterQuestionRemoved(
    savedSnapshotRef: MutableRefObject<SituationGameSnapshot | null>,
    removedQuestionId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterQuestionRemoved(
            savedSnapshotRef.current.questions,
            removedQuestionId
        ),
    };
}

export function patchSavedSituationGameSnapshotAfterChoiceRemoved(
    savedSnapshotRef: MutableRefObject<SituationGameSnapshot | null>,
    questionId: number,
    removedChoiceId: number
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterChoiceRemoved(
            savedSnapshotRef.current.questions,
            questionId,
            removedChoiceId
        ),
    };
}

export function patchSavedSituationGameSnapshotAfterQuestionMediaDeleted(
    savedSnapshotRef: MutableRefObject<SituationGameSnapshot | null>,
    questionId: number,
    field: QuizQuestionMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterQuestionMediaDeleted(
            savedSnapshotRef.current.questions,
            questionId,
            field
        ),
    };
}

export function patchSavedSituationGameSnapshotAfterChoiceMediaDeleted(
    savedSnapshotRef: MutableRefObject<SituationGameSnapshot | null>,
    questionId: number,
    choiceId: number,
    field: QuizChoiceMediaDeleteField
) {
    if (!savedSnapshotRef.current) {
        return;
    }

    savedSnapshotRef.current = {
        ...savedSnapshotRef.current,
        questions: patchQuizQuestionsSnapshotArrayAfterChoiceMediaDeleted(
            savedSnapshotRef.current.questions,
            questionId,
            choiceId,
            field
        ),
    };
}
